import { useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import apiService from 'services/api'
import { featureFlagService } from 'utils/featureFlagService'
import { useLayoutConfig } from 'context/layout'
import { useAuth } from 'context/authentication/useAuth'
import type { BreadcrumbItem, BreadcrumbDropdownItem } from './types'
import type { CompanyCategorySlug, CompanyTypeSlug } from 'models/companies'

interface EntityInfo {
  type: 'chain' | 'door'
  name: string
}

interface UseBreadcrumbsResult {
  items: BreadcrumbItem[]
  isLoading: boolean
  entityInfo?: EntityInfo
}

/**
 * Hook to generate breadcrumb items based on current route and API data.
 *
 * For Targets section: TARGETS (static) | Category (dropdown) | Subcategory (dropdown) | Chain: X | Door: Y
 * For other sections: Section (static/dropdown) | Subsection
 */
export const useBreadcrumbs = (): UseBreadcrumbsResult => {
  const location = useLocation()
  const params = useParams()
  const api = apiService()
  const {
    enableNewNavigation,
    enableEmailSequencing,
    enableReminders,
    enableDocumentLibrary,
  } = featureFlagService()
  const { config } = useLayoutConfig()
  const { user } = useAuth()

  // Fetch categories for Targets section
  const { data: companyCatAndSub, isLoading: categoriesLoading } =
    api.useGetActiveCompanyCategoriesAndTypes()

  // Build breadcrumb items based on current path
  const items = useMemo((): BreadcrumbItem[] => {
    const path = location.pathname
    const breadcrumbs: BreadcrumbItem[] = []

    // === TARGETS SECTION ===
    if (path.startsWith('/targets')) {
      const categorySlug = params.category as CompanyCategorySlug | undefined
      const subCategorySlug = params.sub_category as CompanyTypeSlug | undefined

      // Static "TARGETS" label
      breadcrumbs.push({
        label: 'TARGETS',
        hasDropdown: false,
        isSectionLabel: true,
      })

      // Category dropdown (Commercial, Non-Commercial, Retail)
      if (companyCatAndSub?.length) {
        const activeCategory = companyCatAndSub.find(
          (cat) => cat.slug === categorySlug
        )
        const firstSubOfActiveCategory = activeCategory?.sub_categories?.[0]

        const categoryDropdownItems: BreadcrumbDropdownItem[] =
          companyCatAndSub.map((cat) => {
            const firstSub = cat.sub_categories?.[0]
            return {
              label: cat.name,
              description: cat.description || undefined,
              path: `/targets/${cat.slug}/${firstSub?.slug || ''}`,
              isActive: cat.slug === categorySlug,
            }
          })

        breadcrumbs.push({
          label: activeCategory?.name || 'Category',
          hasDropdown: true,
          dropdownItems: categoryDropdownItems,
        })

        // Subcategory dropdown (Restaurants & Bars, Education - C&U, etc.)
        if (activeCategory?.sub_categories?.length) {
          const activeSubCategory = activeCategory.sub_categories.find(
            (sub) => sub.slug === subCategorySlug
          )

          const subCategoryDropdownItems: BreadcrumbDropdownItem[] =
            activeCategory.sub_categories.map((sub) => ({
              label: sub.name,
              path: `/targets/${categorySlug}/${sub.slug}`,
              isActive: sub.slug === subCategorySlug,
            }))

          breadcrumbs.push({
            label:
              activeSubCategory?.name ||
              firstSubOfActiveCategory?.name ||
              'Subcategory',
            hasDropdown: true,
            dropdownItems: subCategoryDropdownItems,
          })
        }
      }

      return breadcrumbs
    }

    // === ENTITY PAGES (Chain/Door detail pages) ===
    // These are accessed from within Targets, so they should show the Targets breadcrumb trail
    if (isEntityPage(path)) {
      return buildEntityPageBreadcrumbs(
        path,
        companyCatAndSub,
        config.categoryInfo
      )
    }

    // === WORKSPACE SECTION ===
    if (
      path.startsWith('/documents') ||
      path.startsWith('/contacts') ||
      path.startsWith('/activity') ||
      path.startsWith('/reminders') ||
      path.startsWith('/products')
    ) {
      breadcrumbs.push({
        label: 'WORKSPACE',
        hasDropdown: false,
        isSectionLabel: true,
      })

      const workspaceItems: BreadcrumbDropdownItem[] = []

      if (enableDocumentLibrary) {
        workspaceItems.push({
          label: 'Documents',
          path: '/documents',
          isActive: path.startsWith('/documents'),
        })
        const activeItem = workspaceItems.find((item) => item.isActive)
        breadcrumbs.push({
          label: activeItem?.label || 'Documents',
          hasDropdown: true,
          dropdownItems: workspaceItems,
        })
      }

      workspaceItems.push(
        {
          label: 'Contacts',
          path: '/contacts',
          isActive: path.startsWith('/contacts'),
        },
        {
          label: 'Activity',
          path: '/activity',
          isActive: path.startsWith('/activity'),
        },
        {
          label: 'Products',
          path: enableNewNavigation
            ? '/products'
            : '/settings/platform/products',
          isActive: path.startsWith('/products'),
        }
      )

      if (enableReminders) {
        workspaceItems.push({
          label: 'Reminders',
          path: enableNewNavigation ? '/reminders' : '/activity/reminders/',
          isActive: path.startsWith('/reminders'),
        })
      }

      return breadcrumbs
    }

    // === SETTINGS SECTION ===
    if (path.startsWith('/settings')) {
      breadcrumbs.push({
        label: 'SETTINGS',
        hasDropdown: false,
        isSectionLabel: true,
      })

      const showEmailOutreach =
        enableEmailSequencing && user?.company?.has_bison_workspace

      const settingsItems: BreadcrumbDropdownItem[] = [
        {
          label: 'Company',
          path: '/settings/company/account',
          isActive: path.includes('/company'),
        },
        {
          label: 'Users',
          path: '/settings/users/users',
          isActive: path.includes('/users'),
        },
        {
          label: 'Platform',
          path: '/settings/platform/products',
          isActive: path.includes('/platform'),
        },
        {
          label: 'Data',
          path: '/settings/data/filters',
          isActive: path.includes('/data'),
        },
        {
          label: 'Notifications',
          path: '/settings/notifications/platform',
          isActive: path.includes('/notifications'),
        },
        {
          label: 'Misc Settings',
          path: '/settings/misc/misc',
          isActive: path.includes('/misc'),
        },
        {
          label: 'Automations',
          path: '/settings/automations/automations',
          isActive: path.includes('/automations'),
        },
        ...(showEmailOutreach
          ? [
              {
                label: 'Email Outreach',
                path: '/settings/email-outreach/sender-emails',
                isActive: path.includes('/email-outreach'),
              },
            ]
          : []),
      ]

      const activeGroup = settingsItems.find((item) => item.isActive)

      breadcrumbs.push({
        label: activeGroup?.label || 'Settings',
        hasDropdown: true,
        dropdownItems: settingsItems,
      })

      return breadcrumbs
    }

    // === PERFORMANCE SECTION ===
    if (path.startsWith('/dashboard')) {
      breadcrumbs.push({
        label: 'PERFORMANCE',
        hasDropdown: false,
        isSectionLabel: true,
      })
      breadcrumbs.push({
        label: 'Dashboard',
        hasDropdown: false,
        path: '/dashboard',
      })
      return breadcrumbs
    }

    // === CAMPAIGNS SECTION ===
    if (path.startsWith('/campaigns')) {
      breadcrumbs.push({
        label: 'CAMPAIGNS',
        hasDropdown: false,
        isSectionLabel: true,
      })
      breadcrumbs.push({
        label: 'All Campaigns',
        hasDropdown: false,
        path: '/campaigns',
      })
      return breadcrumbs
    }

    // === DEALS SECTION ===
    if (path.startsWith('/deals')) {
      breadcrumbs.push({
        label: 'DEALS',
        hasDropdown: false,
        isSectionLabel: true,
      })
      return breadcrumbs
    }

    // === REBATES SECTION ===
    if (path.startsWith('/rebates')) {
      breadcrumbs.push({
        label: 'REBATES',
        hasDropdown: false,
        isSectionLabel: true,
      })
      return breadcrumbs
    }

    // === SAMPLES SECTION ===
    if (path.startsWith('/samples')) {
      breadcrumbs.push({
        label: 'SAMPLES',
        hasDropdown: false,
        isSectionLabel: true,
      })
      return breadcrumbs
    }

    return breadcrumbs
  }, [
    location.pathname,
    params,
    companyCatAndSub,
    enableNewNavigation,
    enableEmailSequencing,
    enableReminders,
    user?.company?.has_bison_workspace,
    config.categoryInfo,
  ])

  return {
    items,
    isLoading: categoriesLoading,
  }
}

/**
 * Check if the current path is an entity detail page (chain or door)
 */
function isEntityPage(path: string): boolean {
  const entityPatterns = [
    /^\/chain\//,
    /^\/restaurant\//,
    /^\/university\//,
    /^\/k12\//,
    /^\/hospital\//,
    /^\/nursinghome\//,
    /^\/lodging\/chain\//,
    /^\/lodging\/door\//,
    /^\/convenience\/chain\//,
    /^\/convenience\/door\//,
    /^\/company\//, // User-generated companies (dynamic category)
    /^\/door\//, // User-generated doors (dynamic category)
  ]
  return entityPatterns.some((pattern) => pattern.test(path))
}

/**
 * Determine the category and subcategory for entity pages based on URL pattern
 */
function getEntityCategoryInfo(path: string): {
  category: CompanyCategorySlug
  subCategory: CompanyTypeSlug
  isChain: boolean
} | null {
  // Restaurant doors
  if (path.startsWith('/restaurant/')) {
    return {
      category: 'commercial',
      subCategory: 'restaurants-bars',
      isChain: false,
    }
  }
  // Restaurant chains
  if (path.startsWith('/chain/')) {
    return {
      category: 'commercial',
      subCategory: 'restaurants-bars',
      isChain: true,
    }
  }
  // Universities
  if (path.startsWith('/university/')) {
    return {
      category: 'non-commercial',
      subCategory: 'education-cu',
      isChain: true,
    }
  }
  // K-12
  if (path.startsWith('/k12/')) {
    return {
      category: 'non-commercial',
      subCategory: 'education-k-12',
      isChain: true,
    }
  }
  // Hospitals
  if (path.startsWith('/hospital/')) {
    return {
      category: 'non-commercial',
      subCategory: 'healthcare-hospitals',
      isChain: true,
    }
  }
  // Nursing homes
  if (path.startsWith('/nursinghome/')) {
    return {
      category: 'non-commercial',
      subCategory: 'healthcare-nursinghomes',
      isChain: true,
    }
  }
  // Lodging chains
  if (path.startsWith('/lodging/chain/')) {
    return {
      category: 'commercial',
      subCategory: 'lodging-hospitality',
      isChain: true,
    }
  }
  // Lodging doors
  if (path.startsWith('/lodging/door/')) {
    return {
      category: 'commercial',
      subCategory: 'lodging-hospitality',
      isChain: false,
    }
  }
  // Convenience chains
  if (path.startsWith('/convenience/chain/')) {
    return { category: 'retail', subCategory: 'convenience', isChain: true }
  }
  // Convenience doors
  if (path.startsWith('/convenience/door/')) {
    return { category: 'retail', subCategory: 'convenience', isChain: false }
  }
  return null
}

/**
 * Build breadcrumbs for entity detail pages (chain/door pages)
 */
function buildEntityPageBreadcrumbs(
  path: string,
  companyCatAndSub:
    | Array<{
        slug: string
        name: string
        description?: string
        sub_categories: Array<{ slug: string; name: string }>
      }>
    | undefined,
  dynamicCategoryInfo: { categorySlug: string; subCategorySlug: string } | null
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = []

  // Try static lookup first, fall back to dynamic category info from context
  const staticEntityInfo = getEntityCategoryInfo(path)
  const entityInfo =
    staticEntityInfo ||
    (dynamicCategoryInfo
      ? {
          category: dynamicCategoryInfo.categorySlug as CompanyCategorySlug,
          subCategory: dynamicCategoryInfo.subCategorySlug as CompanyTypeSlug,
          isChain: path.startsWith('/company/'), // /company/ = chain-level, /door/ = door-level
        }
      : null)

  if (!entityInfo) return breadcrumbs

  // Static "TARGETS" label
  breadcrumbs.push({
    label: 'TARGETS',
    hasDropdown: false,
    isSectionLabel: true,
  })

  if (companyCatAndSub?.length) {
    const activeCategory = companyCatAndSub.find(
      (cat) => cat.slug === entityInfo.category
    )

    // Category dropdown
    const categoryDropdownItems: BreadcrumbDropdownItem[] =
      companyCatAndSub.map((cat) => {
        const firstSub = cat.sub_categories?.[0]
        return {
          label: cat.name,
          description: cat.description || undefined,
          path: `/targets/${cat.slug}/${firstSub?.slug || ''}`,
          isActive: cat.slug === entityInfo.category,
        }
      })

    breadcrumbs.push({
      label: activeCategory?.name || 'Category',
      hasDropdown: true,
      dropdownItems: categoryDropdownItems,
    })

    // Subcategory dropdown
    if (activeCategory?.sub_categories?.length) {
      const activeSubCategory = activeCategory.sub_categories.find(
        (sub) => sub.slug === entityInfo.subCategory
      )

      const subCategoryDropdownItems: BreadcrumbDropdownItem[] =
        activeCategory.sub_categories.map((sub) => ({
          label: sub.name,
          path: `/targets/${entityInfo.category}/${sub.slug}`,
          isActive: sub.slug === entityInfo.subCategory,
        }))

      breadcrumbs.push({
        label: activeSubCategory?.name || 'Subcategory',
        hasDropdown: true,
        dropdownItems: subCategoryDropdownItems,
      })
    }
  }

  // Chain/Door label will be added by the page component that has access to the entity data
  // This hook returns a placeholder that the page can fill in
  // The entity name breadcrumb is handled separately via entityBreadcrumb prop

  return breadcrumbs
}

export default useBreadcrumbs
